import { defineComponent, ref, getCurrentInstance, inject, h, shallowRef, Teleport } from 'vue';
import { setupConfig, modalController, popoverController } from '@neutron/core';
export { NeutronSafeString, createAnimation, createGesture, drawerController, getPlatforms, getTimeGivenProgression, isPlatform, modalController, popoverController, transitionAnimation } from '@neutron/core';
import { applyPolyfills, defineCustomElements } from '@neutron/core/loader';

const UPDATE_VALUE_EVENT = 'update:modelValue';
const MODEL_VALUE = 'modelValue';
const ROUTER_LINK_VALUE = 'routerLink';
const NAV_MANAGER = 'navManager';
const ROUTER_PROP_REFIX = 'router';
const getComponentClasses = (classes) => {
    return (classes === null || classes === void 0 ? void 0 : classes.split(' ')) || [];
};
const getElementClasses = (ref, componentClasses, defaultClasses = []) => {
    var _a;
    return [...Array.from(((_a = ref.value) === null || _a === void 0 ? void 0 : _a.classList) || []), ...defaultClasses]
        .filter((c, i, self) => !componentClasses.has(c) && self.indexOf(c) === i);
};
/**
* Create a callback to define a Vue component wrapper around a Web Component.
*
* @prop name - The component tag name (i.e. `ion-button`)
* @prop componentProps - An array of properties on the
* component. These usually match up with the @Prop definitions
* in each component's TSX file.
* @prop componentOptions - An object that defines additional
* options for the component such as router or v-model
* integrations.
*/
const defineContainer = (name, componentProps = [], componentOptions = {}) => {
    const { modelProp, modelUpdateEvent, externalModelUpdateEvent } = componentOptions;
    /**
    * Create a Vue component wrapper around a Web Component.
    * Note: The `props` here are not all properties on a component.
    * They refer to whatever properties are set on an instance of a component.
    */
    const Container = defineComponent((props, { attrs, slots, emit }) => {
        var _a;
        let modelPropValue = props[modelProp];
        const containerRef = ref();
        const classes = new Set(getComponentClasses(attrs.class));
        const onVnodeBeforeMount = (vnode) => {
            // Add a listener to tell Vue to update the v-model
            if (vnode.el) {
                const eventsNames = Array.isArray(modelUpdateEvent) ? modelUpdateEvent : [modelUpdateEvent];
                eventsNames.forEach((eventName) => {
                    vnode.el.addEventListener(eventName.toLowerCase(), (e) => {
                        modelPropValue = (e === null || e === void 0 ? void 0 : e.target)[modelProp];
                        emit(UPDATE_VALUE_EVENT, modelPropValue);
                        /**
                         * We need to emit the change event here
                         * rather than on the web component to ensure
                         * that any v-model bindings have been updated.
                         * Otherwise, the developer will listen on the
                         * native web component, but the v-model will
                         * not have been updated yet.
                         */
                        emit(externalModelUpdateEvent, e);
                    });
                });
            }
        };
        const currentInstance = getCurrentInstance();
        const hasRouter = (_a = currentInstance === null || currentInstance === void 0 ? void 0 : currentInstance.appContext) === null || _a === void 0 ? void 0 : _a.provides[NAV_MANAGER];
        const navManager = hasRouter ? inject(NAV_MANAGER) : undefined;
        const handleRouterLink = (ev) => {
            const { routerLink } = props;
            if (!routerLink)
                return;
            const routerProps = Object.keys(props).filter(p => p.startsWith(ROUTER_PROP_REFIX));
            if (navManager !== undefined) {
                let navigationPayload = { event: ev };
                routerProps.forEach(prop => {
                    navigationPayload[prop] = props[prop];
                });
                navManager.navigate(navigationPayload);
            }
            else {
                console.warn('Tried to navigate, but no router was found. Make sure you have mounted Vue Router.');
            }
        };
        return () => {
            modelPropValue = props[modelProp];
            getComponentClasses(attrs.class).forEach(value => {
                classes.add(value);
            });
            const oldClick = props.onClick;
            const handleClick = (ev) => {
                if (oldClick !== undefined) {
                    oldClick(ev);
                }
                if (!ev.defaultPrevented) {
                    handleRouterLink(ev);
                }
            };
            let propsToAdd = Object.assign(Object.assign({}, props), { ref: containerRef, class: getElementClasses(containerRef, classes), onClick: handleClick, onVnodeBeforeMount: (modelUpdateEvent && externalModelUpdateEvent) ? onVnodeBeforeMount : undefined });
            if (modelProp) {
                /**
                 * Starting in Vue 3.1.0, all properties are
                 * added as keys to the props object, even if
                 * they are not being used. In order to correctly
                 * account for both value props and v-model props,
                 * we need to check if the key exists for Vue <3.1.0
                 * and then check if it is not undefined for Vue >= 3.1.0.
                 */
                propsToAdd = Object.assign(Object.assign({}, propsToAdd), { [modelProp]: props.hasOwnProperty(MODEL_VALUE) && props[MODEL_VALUE] !== undefined ? props.modelValue : modelPropValue });
            }
            return h(name, propsToAdd, slots.default && slots.default());
        };
    });
    Container.displayName = name;
    Container.props = [...componentProps, ROUTER_LINK_VALUE];
    if (modelProp) {
        Container.props.push(MODEL_VALUE);
        Container.emits = [UPDATE_VALUE_EVENT, externalModelUpdateEvent];
    }
    return Container;
};

/* eslint-disable */
const NeuAccordion = /*@__PURE__*/ defineContainer('neu-accordion', [
    'color',
    'single'
]);
const NeuAccordionContent = /*@__PURE__*/ defineContainer('neu-accordion-content', [
    'ariaLabelledby',
    'focusable'
]);
const NeuAccordionHeader = /*@__PURE__*/ defineContainer('neu-accordion-header', [
    'disabled',
    'icon',
    'iconStyle',
    'iconLoad'
]);
const NeuAccordionItem = /*@__PURE__*/ defineContainer('neu-accordion-item', [
    'selected'
]);
const NeuAvatar = /*@__PURE__*/ defineContainer('neu-avatar', [
    'color',
    'initials'
]);
const NeuBackdrop = /*@__PURE__*/ defineContainer('neu-backdrop', [
    'stopPropagation',
    'tappable',
    'visible',
    'neuBackdropTap'
]);
const NeuBadge = /*@__PURE__*/ defineContainer('neu-badge', [
    'color',
    'content'
]);
const NeuBreadcrumb = /*@__PURE__*/ defineContainer('neu-breadcrumb', [
    'links'
]);
const NeuButton = /*@__PURE__*/ defineContainer('neu-button', [
    'buttonType',
    'color',
    'disabled',
    'download',
    'fill',
    'full',
    'href',
    'name',
    'rel',
    'search',
    'shape',
    'size',
    'target',
    'type',
    'value',
    'neuFocus',
    'neuBlur'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuButtonToggle = /*@__PURE__*/ defineContainer('neu-button-toggle', [
    'btnMax',
    'width',
    'value',
    'neuChange'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuButtons = /*@__PURE__*/ defineContainer('neu-buttons');
const NeuCard = /*@__PURE__*/ defineContainer('neu-card', [
    'button',
    'color',
    'dialog',
    'disabled',
    'download',
    'height',
    'href',
    'maxHeight',
    'maxWidth',
    'minHeight',
    'minWidth',
    'padding',
    'rel',
    'small',
    'target',
    'type',
    'width'
]);
const NeuCardActions = /*@__PURE__*/ defineContainer('neu-card-actions', [
    'center',
    'right',
    'small'
]);
const NeuCardContent = /*@__PURE__*/ defineContainer('neu-card-content', [
    'center',
    'right'
]);
const NeuCardHeader = /*@__PURE__*/ defineContainer('neu-card-header', [
    'center',
    'color',
    'full',
    'right'
]);
const NeuCardSubtitle = /*@__PURE__*/ defineContainer('neu-card-subtitle', [
    'color'
]);
const NeuCardTitle = /*@__PURE__*/ defineContainer('neu-card-title', [
    'color',
    'dark'
]);
const NeuCheckbox = /*@__PURE__*/ defineContainer('neu-checkbox', [
    'checked',
    'color',
    'disabled',
    'name',
    'value',
    'neuChange',
    'neuFocus',
    'neuBlur',
    'neuStyle'
], {
    "modelProp": "checked",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuChip = /*@__PURE__*/ defineContainer('neu-chip', [
    'avatar',
    'color',
    'disabled',
    'icon',
    'removable',
    'selectable',
    'selected',
    'value',
    'neuChange',
    'neuFocus',
    'neuBlur',
    'neuStyle'
]);
const NeuCol = /*@__PURE__*/ defineContainer('neu-col', [
    'alignSelf',
    'cols',
    'lg',
    'md',
    'offset',
    'offsetLg',
    'offsetMd',
    'offsetSm',
    'offsetXl',
    'offsetXxl',
    'order',
    'orderLg',
    'orderMd',
    'orderSm',
    'orderXl',
    'orderXxl',
    'sm',
    'xl',
    'xxl'
]);
const NeuContainer = /*@__PURE__*/ defineContainer('neu-container', [
    'fluid'
]);
const NeuContent = /*@__PURE__*/ defineContainer('neu-content', [
    'fullscreen',
    'nav',
    'scrollX',
    'scrollY',
    'scrollEvents',
    'neuScrollStart',
    'neuScroll',
    'neuScrollEnd'
]);
const NeuDivider = /*@__PURE__*/ defineContainer('neu-divider', [
    'color',
    'light',
    'dark',
    'selected'
]);
const NeuDrawer = /*@__PURE__*/ defineContainer('neu-drawer', [
    'contentId',
    'disabled',
    'drawerId',
    'maxEdgeStart',
    'mainNav',
    'side',
    'subNav',
    'swipeGesture',
    'type',
    'neuWillOpen',
    'neuWillClose',
    'neuDidOpen',
    'neuDidClose',
    'neuDrawerChange'
]);
const NeuDrawerButton = /*@__PURE__*/ defineContainer('neu-drawer-button', [
    'autoHide',
    'color',
    'disabled',
    'drawer',
    'icon',
    'stacked',
    'text',
    'type',
    'neuFocus',
    'neuBlur'
]);
const NeuDrawerToggle = /*@__PURE__*/ defineContainer('neu-drawer-toggle', [
    'drawer',
    'autoHide'
]);
const NeuDropdown = /*@__PURE__*/ defineContainer('neu-dropdown', [
    'active',
    'disabled',
    'download',
    'dropdown',
    'href',
    'large',
    'layout',
    'navy',
    'multiple',
    'rel',
    'selected',
    'tab',
    'target',
    'white',
    'neuChange',
    'neuTabDropdownChange',
    'neuStyle'
]);
const NeuFab = /*@__PURE__*/ defineContainer('neu-fab', [
    'edge',
    'horizontal',
    'vertical'
]);
const NeuFabButton = /*@__PURE__*/ defineContainer('neu-fab-button', [
    'color',
    'disabled',
    'download',
    'href',
    'rel',
    'target',
    'type',
    'neuFocus',
    'neuBlur',
    'neuChange'
]);
const NeuFileUploader = /*@__PURE__*/ defineContainer('neu-file-uploader', [
    'accept',
    'audio',
    'csv',
    'doc',
    'excel',
    'image',
    'limit',
    'multiple',
    'pdf',
    'text',
    'video',
    'neuChange'
]);
const NeuFooter = /*@__PURE__*/ defineContainer('neu-footer', [
    'color'
]);
const NeuHeader = /*@__PURE__*/ defineContainer('neu-header');
const NeuHint = /*@__PURE__*/ defineContainer('neu-hint', [
    'color',
    'error',
    'hintText',
    'info',
    'success',
    'warning'
]);
const NeuIcon = /*@__PURE__*/ defineContainer('neu-icon', [
    'btn',
    'color',
    'disabled',
    'feedback',
    'hint',
    'large',
    'left',
    'right',
    'small'
]);
const NeuImage = /*@__PURE__*/ defineContainer('neu-image', [
    'alt',
    'borderColor',
    'borderRadius',
    'borderStyle',
    'borderWidth',
    'fill',
    'fluid',
    'gradient',
    'height',
    'landscape',
    'maxHeight',
    'minHeight',
    'maxWidth',
    'minWidth',
    'src',
    'square',
    'width'
]);
const NeuInput = /*@__PURE__*/ defineContainer('neu-input', [
    'accept',
    'autocapitalize',
    'autocomplete',
    'autocorrect',
    'clearable',
    'clearOnEdit',
    'debounce',
    'disabled',
    'enterkeyhint',
    'error',
    'fireFocusEvents',
    'icon',
    'inputmode',
    'max',
    'maxlength',
    'min',
    'minlength',
    'multiple',
    'name',
    'pattern',
    'placeholder',
    'readonly',
    'required',
    'requiredText',
    'size',
    'spellcheck',
    'step',
    'type',
    'value',
    'neuBlur',
    'neuChange',
    'neuFocus',
    'neuInput',
    'neuStyle'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuItem = /*@__PURE__*/ defineContainer('neu-item', [
    'color',
    'button',
    'detail',
    'detailIcon',
    'disabled',
    'download',
    'focusable',
    'href',
    'lines',
    'nav',
    'rel',
    'selected',
    'target',
    'type'
]);
const NeuLabel = /*@__PURE__*/ defineContainer('neu-label', [
    'color',
    'content',
    'for',
    'input',
    'position',
    'neuColor',
    'neuStyle'
]);
const NeuLinearFeedback = /*@__PURE__*/ defineContainer('neu-linear-feedback', [
    'ariaLabel',
    'ariaLabelledby',
    'color',
    'continuous',
    'indeterminate',
    'max',
    'progress'
]);
const NeuLink = /*@__PURE__*/ defineContainer('neu-link', [
    'ariaLabel',
    'color',
    'dark',
    'download',
    'href',
    'nav',
    'rel',
    'target',
    'neuStyle'
]);
const NeuList = /*@__PURE__*/ defineContainer('neu-list', [
    'color',
    'inset',
    'lines'
]);
const NeuNotification = /*@__PURE__*/ defineContainer('neu-notification', [
    'dark',
    'light',
    'neuChange'
]);
const NeuOption = /*@__PURE__*/ defineContainer('neu-option', [
    'disabled',
    'group',
    'name',
    'value',
    'neuStyle',
    'neuFocus',
    'neuBlur'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuOptionGroup = /*@__PURE__*/ defineContainer('neu-option-group', [
    'name'
]);
const NeuOptionsList = /*@__PURE__*/ defineContainer('neu-options-list', [
    'allowEmptySelection',
    'header',
    'message',
    'name',
    'options',
    'subHeader',
    'value',
    'neuChange'
]);
const NeuPaginator = /*@__PURE__*/ defineContainer('neu-paginator', [
    'currentPage',
    'rowsPerPage',
    'total',
    'neuChange'
]);
const NeuRadio = /*@__PURE__*/ defineContainer('neu-radio', [
    'color',
    'disabled',
    'name',
    'value',
    'neuStyle',
    'neuFocus',
    'neuBlur'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuRadioGroup = /*@__PURE__*/ defineContainer('neu-radio-group', [
    'allowEmptySelection',
    'name',
    'value',
    'neuChange'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuRow = /*@__PURE__*/ defineContainer('neu-row', [
    'align',
    'alignContent',
    'alignContentLg',
    'alignContentMd',
    'alignContentSm',
    'alignContentXl',
    'alignContentXxl',
    'alignLg',
    'alignMd',
    'alignSm',
    'alignXl',
    'alignXxl',
    'dense',
    'justify',
    'justifyLg',
    'justifyMd',
    'justifySm',
    'justifyXl',
    'justifyXxl',
    'noGutters'
]);
const NeuSearchbar = /*@__PURE__*/ defineContainer('neu-searchbar', [
    'autocomplete',
    'autocorrect',
    'button',
    'clearable',
    'color',
    'debounce',
    'disabled',
    'inputmode',
    'enterkeyhint',
    'maxWidth',
    'minWidth',
    'placeholder',
    'spellcheck',
    'type',
    'value',
    'width',
    'neuInput',
    'neuChange',
    'neuClear',
    'neuBlur',
    'neuFocus',
    'neuStyle'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuSelect = /*@__PURE__*/ defineContainer('neu-select', [
    'compact',
    'compareWith',
    'disabled',
    'error',
    'hint',
    'interface',
    'interfaceOptions',
    'multiple',
    'name',
    'placeholder',
    'required',
    'requiredText',
    'selectedText',
    'value',
    'neuBlur',
    'neuCancel',
    'neuChange',
    'neuFocus',
    'neuStyle'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuSelectNative = /*@__PURE__*/ defineContainer('neu-select-native', [
    'ariaLabel',
    'compact',
    'disabled',
    'error',
    'hint',
    'name',
    'options',
    'required',
    'requiredText',
    'value',
    'simple',
    'size',
    'neuBlur',
    'neuChange',
    'neuFocus',
    'neuStyle'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuShowHide = /*@__PURE__*/ defineContainer('neu-show-hide', [
    'active',
    'default',
    'password',
    'show',
    'toggle'
]);
const NeuSnackbar = /*@__PURE__*/ defineContainer('neu-snackbar', [
    'feedback',
    'width',
    'maxWidth',
    'minWidth'
]);
const NeuSpacer = /*@__PURE__*/ defineContainer('neu-spacer');
const NeuSpinner = /*@__PURE__*/ defineContainer('neu-spinner', [
    'ariaLabel',
    'btn',
    'btnLg',
    'color',
    'default',
    'xl',
    'height',
    'huge',
    'lg',
    'md',
    'sm',
    'tiny',
    'width'
]);
const NeuStepper = /*@__PURE__*/ defineContainer('neu-stepper', [
    'type',
    'steps',
    'neuChange'
]);
const NeuSubLeftNav = /*@__PURE__*/ defineContainer('neu-sub-left-nav', [
    'color',
    'previousPage',
    'name',
    'overlay',
    'side',
    'neuChange'
]);
const NeuTab = /*@__PURE__*/ defineContainer('neu-tab', [
    'active',
    'disabled',
    'download',
    'href',
    'large',
    'layout',
    'navy',
    'rel',
    'tab',
    'target',
    'white',
    'neuTabChange',
    'neuStyle'
]);
const NeuTable = /*@__PURE__*/ defineContainer('neu-table');
const NeuTableBody = /*@__PURE__*/ defineContainer('neu-table-body', [
    'maxHeight'
]);
const NeuTableHeading = /*@__PURE__*/ defineContainer('neu-table-heading', [
    'active',
    'icon',
    'neuChange'
]);
const NeuTableNative = /*@__PURE__*/ defineContainer('neu-table-native', [
    'unit',
    'header',
    'fixed',
    'size',
    'sort',
    'direction',
    'caption'
]);
const NeuTableRow = /*@__PURE__*/ defineContainer('neu-table-row', [
    'align',
    'color',
    'columns',
    'danger',
    'ellipsis',
    'header',
    'headerSize',
    'indent',
    'selected',
    'size',
    'striped',
    'neuStyle',
    'neuChange'
]);
const NeuTableRowCount = /*@__PURE__*/ defineContainer('neu-table-row-count', [
    'pageNumber',
    'rowsPerPage',
    'section',
    'default',
    'selections',
    'total',
    'neuChange'
]);
const NeuTablist = /*@__PURE__*/ defineContainer('neu-tablist', [
    'color',
    'value',
    'neuChange'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuTag = /*@__PURE__*/ defineContainer('neu-tag', [
    'clickable',
    'color',
    'danger',
    'disabled',
    'primary',
    'secondary',
    'success',
    'removable',
    'warning',
    'neuChange',
    'neuFocus',
    'neuBlur',
    'neuStyle'
]);
const NeuTextarea = /*@__PURE__*/ defineContainer('neu-textarea', [
    'autocapitalize',
    'autoGrow',
    'clearOnEdit',
    'cols',
    'debounce',
    'disabled',
    'enterkeyhint',
    'error',
    'fireFocusEvents',
    'hint',
    'inputmode',
    'maxlength',
    'minlength',
    'name',
    'placeholder',
    'readonly',
    'required',
    'requiredText',
    'rows',
    'spellcheck',
    'value',
    'wrap',
    'neuBlur',
    'neuChange',
    'neuFocus',
    'neuInput',
    'neuStyle'
], {
    "modelProp": "value",
    "modelUpdateEvent": [
        "v-neuChange",
        "v-neu-change"
    ],
    "externalModelUpdateEvent": "neuChange"
});
const NeuTitle = /*@__PURE__*/ defineContainer('neu-title', [
    'color',
    'size',
    'neuStyle'
]);
const NeuToolbar = /*@__PURE__*/ defineContainer('neu-toolbar', [
    'bottom',
    'compact',
    'color',
    'fluid',
    'nav',
    'elevation',
    'top'
]);
const NeuTooltip = /*@__PURE__*/ defineContainer('neu-tooltip', [
    'ariaDescribedBy'
]);
const NeuUserControls = /*@__PURE__*/ defineContainer('neu-user-controls', [
    'dark',
    'drawer',
    'href',
    'light',
    'logout',
    'user',
    'neuChange'
]);

// type LIFECYCLE_EVENTS =
//   | typeof LIFECYCLE_WILL_ENTER
//   | typeof LIFECYCLE_DID_ENTER
//   | typeof LIFECYCLE_WILL_LEAVE
//   | typeof LIFECYCLE_DID_LEAVE;
var LifecycleHooks;
(function (LifecycleHooks) {
    LifecycleHooks["WillEnter"] = "onNeuViewWillEnter";
    LifecycleHooks["DidEnter"] = "onNeuViewDidEnter";
    LifecycleHooks["WillLeave"] = "onNeuViewWillLeave";
    LifecycleHooks["DidLeave"] = "onNeuViewDidLeave";
})(LifecycleHooks || (LifecycleHooks = {}));
const needsKebabCase = (version) => !['3.0.0', '3.0.1', '3.0.2', '3.0.3', '3.0.4', '3.0.5'].includes(version);

/**
 * We need to make sure that the web component fires an event
 * that will not conflict with the user's @neuChange binding,
 * otherwise the binding's callback will fire before any
 * v-model values have been updated.
 */
const toLowerCase = (eventName) => eventName === 'neuChange' ? 'v-neuchange' : eventName.toLowerCase();
const toKebabCase = (eventName) => eventName === 'neuChange'
    ? 'v-neu-change'
    : eventName.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
/**
 * Vue 3.0.6 fixed a bug where events on custom elements
 * were always converted to lower case, so "neuRefresh"
 * became "neuRefresh". We need to account for the old
 * issue as well as the new behavior where "neuRefresh"
 * is converted to "neu-refresh".
 * See https://github.com/vuejs/vue-next/pull/2847
 */
const getHelperFunctions = (needsKebabCase = true) => {
    const conversionFn = needsKebabCase ? toKebabCase : toLowerCase;
    return {
        ael: (el, eventName, cb, opts) => el.addEventListener(conversionFn(eventName), cb, opts),
        rel: (el, eventName, cb, opts) => el.removeEventListener(conversionFn(eventName), cb, opts),
        ce: (eventName, opts) => new CustomEvent(conversionFn(eventName), opts)
    };
};
const NeutronVue = {
    async install(app, config = {}) {
        if (typeof window !== 'undefined') {
            const { ael, rel, ce } = getHelperFunctions(needsKebabCase(app.version));
            setupConfig(Object.assign(Object.assign({}, config), { _ael: ael, _rel: rel }));
            await applyPolyfills();
            await defineCustomElements(window, {
                exclude: [],
                ce,
                ael,
                rel
            });
        }
    }
};

const userComponents = shallowRef([]);
const NeuApp = defineComponent({
    name: 'NeuApp',
    setup(_, { attrs, slots }) {
        return () => {
            return h('neu-app', Object.assign({}, attrs), [slots.default && slots.default(), ...userComponents.value]);
        };
    }
});
/**
 * When rendering user components inside of
 * neu-modal, or neu-popover the component
 * needs to be created inside of the current application
 * context otherwise libraries such as vue-i18n or vuex
 * will not work properly.
 *
 * `userComponents` renders teleported components as children
 * of `neu-app` within the current application context.
 */
const addTeleportedUserComponent = (component) => {
    userComponents.value = [...userComponents.value, component];
};
const removeTeleportedUserComponent = (component) => {
    userComponents.value = userComponents.value.filter(cmp => cmp !== component);
};

/**
 * Make sure we only
 * warn user about each
 * event at most once.
 */
let willPresentWarn = false;
let didPresentWarn = false;
let willDismissWarn = false;
let didDismissWarn = false;
const checkForDeprecatedListeners = (instance) => {
    const props = instance.vnode.props;
    if (!willPresentWarn && props.onOnWillPresent !== undefined) {
        console.warn('[@neutron/vue Deprecation]: @onWillPresent has been deprecated in favor of @willPresent and will be removed.');
        willPresentWarn = true;
    }
    if (!didPresentWarn && props.onOnDidPresent !== undefined) {
        console.warn('[@neutron/vue Deprecation]: @onDidPresent has been deprecated in favor of @didPresent and will be removed.');
        didPresentWarn = true;
    }
    if (!willDismissWarn && props.onOnWillDismiss !== undefined) {
        console.warn('[@neutron/vue Deprecation]: @onWillDismiss has been deprecated in favor of @willDismiss and will be removed.');
        willDismissWarn = true;
    }
    if (!didDismissWarn && props.onOnDidDismiss !== undefined) {
        console.warn('[@neutron/vue Deprecation]: @onDidDismiss has been deprecated in favor of @didDismiss and will be removed.');
        didDismissWarn = true;
    }
};
const defineOverlayContainer = (name, componentProps = [], controller) => {
    /**
     * Vue 3.0.6 fixed a bug where events on custom elements
     * were always converted to lower case, so "neuRefresh"
     * became "neurefresh". We need to account for the old
     * issue as well as the new behavior where "neuRefresh"
     * is converted to "neu-refresh".
     * See https://github.com/vuejs/vue-next/pull/2847
     */
    const eventPrefix = name.toLowerCase().split('-').join('');
    const lowerCaseListeners = [
        {
            componentEv: `${eventPrefix}willpresent`,
            frameworkEv: 'willPresent',
            deprecatedEv: 'onWillPresent'
        },
        {
            componentEv: `${eventPrefix}didpresent`,
            frameworkEv: 'didPresent',
            deprecatedEv: 'onDidPresent'
        },
        {
            componentEv: `${eventPrefix}willdismiss`,
            frameworkEv: 'willDismiss',
            deprecatedEv: 'onWillDismiss'
        },
        {
            componentEv: `${eventPrefix}diddismiss`,
            frameworkEv: 'didDismiss',
            deprecatedEv: 'onDidDismiss'
        }
    ];
    const kebabCaseListeners = [
        {
            componentEv: `${name}-will-present`,
            frameworkEv: 'willPresent',
            deprecatedEv: 'onWillPresent'
        },
        { componentEv: `${name}-did-present`, frameworkEv: 'didPresent', deprecatedEv: 'onDidPresent' },
        {
            componentEv: `${name}-will-dismiss`,
            frameworkEv: 'willDismiss',
            deprecatedEv: 'onWillDismiss'
        },
        { componentEv: `${name}-did-dismiss`, frameworkEv: 'didDismiss', deprecatedEv: 'onDidDismiss' }
    ];
    const Container = defineComponent((props, { slots, emit }) => {
        const instance = getCurrentInstance();
        const adjustedEventListeners = needsKebabCase(instance.appContext.app.version)
            ? kebabCaseListeners
            : lowerCaseListeners;
        checkForDeprecatedListeners(instance);
        const overlay = ref();
        const onVnodeMounted = async () => {
            const isOpen = props.isOpen;
            isOpen && (await present(props));
        };
        const onVnodeUpdated = async (node, prevNode) => {
            const isOpen = node.props.isOpen;
            const prevIsOpen = prevNode.props.isOpen;
            /**
             * Do not do anything if this prop
             * did not change.
             */
            if (isOpen === prevIsOpen)
                return;
            if (isOpen) {
                await present(props);
            }
            else {
                await dismiss();
            }
        };
        const onVnodeBeforeUnmount = async () => {
            await dismiss();
        };
        const dismiss = async () => {
            if (!overlay.value)
                return;
            await overlay.value;
            overlay.value = overlay.value.dismiss();
            await overlay.value;
            overlay.value = undefined;
        };
        const present = async (props) => {
            var _a;
            /**
             * Do not open another instance
             * if one is already opened.
             */
            if (overlay.value) {
                await overlay.value;
            }
            if ((_a = overlay.value) === null || _a === void 0 ? void 0 : _a.present) {
                await overlay.value.present();
                return;
            }
            /**
             * When supporting both the "on" prefixed and non-"on" prefixed
             * events, there seems to be an issue where the handlers are
             * getting passed as props. This should be resolved when we drop
             * support for the "on" prefixed listeners.
             */
            const restOfProps = Object.assign({}, props);
            delete restOfProps.onWillPresent;
            delete restOfProps.onDidPresent;
            delete restOfProps.onWillDismiss;
            delete restOfProps.onDidDismiss;
            const component = slots.default && slots.default()[0];
            overlay.value = controller.create(Object.assign(Object.assign({}, restOfProps), { component }));
            overlay.value = await overlay.value;
            adjustedEventListeners.forEach(eventListener => {
                overlay.value.addEventListener(eventListener.componentEv, () => {
                    emit(eventListener.frameworkEv);
                    emit(eventListener.deprecatedEv);
                });
            });
            await overlay.value.present();
        };
        return () => {
            return h('div', {
                style: { display: 'none' },
                onVnodeMounted,
                onVnodeUpdated,
                onVnodeBeforeUnmount,
                isOpen: props.isOpen
            });
        };
    });
    Container.displayName = name;
    Container.props = [...componentProps, 'isOpen'];
    Container.emits = [
        'willPresent',
        'didPresent',
        'willDismiss',
        'didDismiss',
        'onWillPresent',
        'onDidPresent',
        'onWillDismiss',
        'onDidDismiss'
    ];
    return Container;
};

/* auto-generated vue overlay proxies */
const NeuModal = /*@__PURE__*/ defineOverlayContainer('neu-modal', ['animated', 'backdropDismiss', 'card', 'color', 'component', 'componentProps', 'cssClass', 'enterAnimation', 'height', 'htmlAttributes', 'keyboardClose', 'leaveAnimation', 'maxHeight', 'maxWidth', 'minHeight', 'minWidth', 'padding', 'presentingElement', 'search', 'showBackdrop', 'small', 'swipeToClose', 'width'], modalController);
const NeuPopover = /*@__PURE__*/ defineOverlayContainer('neu-popover', ['animated', 'backdropDismiss', 'component', 'componentProps', 'cssClass', 'enterAnimation', 'event', 'htmlAttributes', 'keyboardClose', 'leaveAnimation', 'showBackdrop'], popoverController);

/**
 * Creates an returns a function that
 * can be used to provide a lifecycle hook.
 */
const injectHook = (lifecycleType, hook, component
// @ts-ignore
) => {
    if (component) {
        const target = component;
        const hooks = target.proxy[lifecycleType] || (target.proxy[lifecycleType] = []);
        const wrappedHook = (...args) => {
            if (target.isUnmounted) {
                return;
            }
            return args ? hook(...args) : hook();
        };
        hooks.push(wrappedHook);
        return wrappedHook;
    }
    else {
        console.warn('[@neutron/vue]: Neutron Lifecycle Hooks can only be used during execution of setup().');
    }
};
const createHook = (lifecycle) => {
    return (hook, target = getCurrentInstance()) => injectHook(lifecycle, hook, target);
};
const onNeuViewWillEnter = createHook(LifecycleHooks.WillEnter);
const onNeuViewDidEnter = createHook(LifecycleHooks.DidEnter);
const onNeuViewWillLeave = createHook(LifecycleHooks.WillLeave);
const onNeuViewDidLeave = createHook(LifecycleHooks.DidLeave);

const VueDelegate = (addFn = addTeleportedUserComponent, removeFn = removeTeleportedUserComponent) => {
    let Component;
    const attachViewToDom = (parentElement, component, componentProps = {}, classes) => {
        /**
         * Neutron Framework passes in modal and popover element
         * refs as props, but if these are not defined
         * on the Vue component instance as props, Vue will
         * warn the user.
         */
        delete componentProps['modal'];
        delete componentProps['popover'];
        const div = document.createElement('div');
        classes && div.classList.add(...classes);
        parentElement.appendChild(div);
        Component = h(Teleport, { to: div }, h(component, Object.assign({}, componentProps)));
        addFn(Component);
        return div;
    };
    const removeViewFromDom = () => {
        Component && removeFn(Component);
        return Promise.resolve();
    };
    return { attachViewToDom, removeViewFromDom };
};

const oldModalCreate = modalController.create.bind(modalController);
modalController.create = options => {
    return oldModalCreate(Object.assign(Object.assign({}, options), { delegate: VueDelegate() }));
};
const oldPopoverCreate = popoverController.create.bind(popoverController);
popoverController.create = options => {
    return oldPopoverCreate(Object.assign(Object.assign({}, options), { delegate: VueDelegate() }));
};

export { NeuAccordion, NeuAccordionContent, NeuAccordionHeader, NeuAccordionItem, NeuApp, NeuAvatar, NeuBackdrop, NeuBadge, NeuBreadcrumb, NeuButton, NeuButtonToggle, NeuButtons, NeuCard, NeuCardActions, NeuCardContent, NeuCardHeader, NeuCardSubtitle, NeuCardTitle, NeuCheckbox, NeuChip, NeuCol, NeuContainer, NeuContent, NeuDivider, NeuDrawer, NeuDrawerButton, NeuDrawerToggle, NeuDropdown, NeuFab, NeuFabButton, NeuFileUploader, NeuFooter, NeuHeader, NeuHint, NeuIcon, NeuImage, NeuInput, NeuItem, NeuLabel, NeuLinearFeedback, NeuLink, NeuList, NeuModal, NeuNotification, NeuOption, NeuOptionGroup, NeuOptionsList, NeuPaginator, NeuPopover, NeuRadio, NeuRadioGroup, NeuRow, NeuSearchbar, NeuSelect, NeuSelectNative, NeuShowHide, NeuSnackbar, NeuSpacer, NeuSpinner, NeuStepper, NeuSubLeftNav, NeuTab, NeuTable, NeuTableBody, NeuTableHeading, NeuTableNative, NeuTableRow, NeuTableRowCount, NeuTablist, NeuTag, NeuTextarea, NeuTitle, NeuToolbar, NeuTooltip, NeuUserControls, NeutronVue, onNeuViewDidEnter, onNeuViewDidLeave, onNeuViewWillEnter, onNeuViewWillLeave };
//# sourceMappingURL=index.esm.js.map
